import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "antd";
import {
  AppstoreOutlined,
  LineChartOutlined,
  LogoutOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  SHOW_PORTAL_SELECTION_MODAL,
  getAvailablePortalOptions,
  goToPortal,
} from "feature/authv2/portalDeskConfig";
import { cookieExpiresFromToken, tokenManager } from "services/client/token-manager";
import { textBeautifyFunction } from "controllers/common";
import { useAgentAccountDetails } from "shared/hooks/common";
import { getUserDetailsAPI } from "shared/api/shared";

const COMPANY_LOGO = "/icons/carrum.svg";
const COMPANY_NAME = "Carrum";

const MESSAGE_QUERY_KEYS = ["message", "error"];

function getInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getPortalIcon(portalId) {
  const iconStyle = { fontSize: 34, color: "#4b5563" };
  if (portalId === "carrum") return <AppstoreOutlined style={iconStyle} />;
  if (portalId === "ops") return <SettingOutlined style={iconStyle} />;
  if (portalId === "crm") return <LineChartOutlined style={iconStyle} />;
  return <AppstoreOutlined style={iconStyle} />;
}

/**
 * Standalone route `/desk`: choose app portal after authentication.
 * Requires a session (route `protected: true`). Redirects to / if portal selection is disabled.
 * Optional query `message` or `error` (URL-encoded) — shown when a portal redirects back with a failure reason.
 */
export default function PortalSelect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hasApiFrappeCredentials, setHasApiFrappeCredentials] = useState(false);
  const [isCheckingPortalPermissions, setIsCheckingPortalPermissions] = useState(true);
  const [portalPermissionError, setPortalPermissionError] = useState(false);
  const { agentName, agentRole, agentAccountId, allAgentDetails } = useAgentAccountDetails();
  const portalOptions = useMemo(
    () => getAvailablePortalOptions(allAgentDetails?.role || agentRole, hasApiFrappeCredentials),
    [agentRole, allAgentDetails?.role, hasApiFrappeCredentials]
  );
  const displayName =
    agentName ||
    allAgentDetails?.first_name ||
    allAgentDetails?.name ||
    allAgentDetails?.email ||
    "User";
  const displayRole = agentRole ? textBeautifyFunction(agentRole) : "Role unavailable";
  const userInitials = getInitials(displayName);

  const portalRedirectNotice = useMemo(() => {
    for (const key of MESSAGE_QUERY_KEYS) {
      const raw = searchParams.get(key);
      if (raw == null) continue;
      const trimmed = raw.trim();
      if (trimmed.length > 0) return trimmed;
    }
    return null;
  }, [searchParams]);

  useEffect(() => {
    if (!SHOW_PORTAL_SELECTION_MODAL) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!agentAccountId) {
      setIsCheckingPortalPermissions(false);
      return;
    }

    let isMounted = true;

    const fetchAgentDetails = async () => {
      try {
        setIsCheckingPortalPermissions(true);
        setPortalPermissionError(false);
        const response = await getUserDetailsAPI(agentAccountId);
        const userDetails = response?.data?.data || response?.data?.results || response?.data;
        const frappeCred = userDetails?.frappeCred;
        const { username, password } = frappeCred || {};

        if (username && password) {
          const expiry = cookieExpiresFromToken(tokenManager.getAccess());
          tokenManager.setFrappeCredentials(username, password, expiry);
          if (isMounted) {
            setHasApiFrappeCredentials(true);
          }
        }
      } catch (error) {
        console.error("Unable to fetch agent details:", error);
        if (isMounted) {
          setPortalPermissionError(true);
        }
      } finally {
        if (isMounted) {
          setIsCheckingPortalPermissions(false);
        }
      }
    };

    fetchAgentDetails();

    return () => {
      isMounted = false;
    };
  }, [agentAccountId]);

  const handlePortalSelect = useCallback(
    (portal) => {
      goToPortal(portal, { navigate });
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    tokenManager.clearAll();
    window.location.replace("/login");
  }, []);

  if (!SHOW_PORTAL_SELECTION_MODAL) {
    return null;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "#fff",
        padding: 24,
      }}
    >
      <img
        src={COMPANY_LOGO}
        alt={`${COMPANY_NAME} logo`}
        style={{ height: 65, width: "auto", objectFit: "contain", marginBottom: 25 }}
      />
      <div
        style={{
          width: "100%",
          maxWidth: 650,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          border: "1px solid #e5e7eb",
        }}
        className="portal-selection-page"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "16px 22px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "#f3f4f6",
                color: "#374151",
                fontSize: 14,
                fontWeight: 700,
                flex: "0 0 auto",
              }}
            >
              {userInitials}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
                color: "#111827",
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 220,
                }}
              >
                {displayName}
              </span>
              <span style={{ color: "#bfbfbf", fontWeight: 700 }}>·</span>
              <span
                style={{ fontSize: 14, color: "#595959", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                {displayRole}
              </span>
            </div>
          </div>
          <Button
            type="default"
            size="middle"
            shape="round"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              borderColor: "#d1d5db",
              color: "#374151",
              fontWeight: 600,
              height: 38,
              paddingInline: 18,
              flex: "0 0 auto",
            }}
          >
            Logout
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "26px 24px 34px",
          }}
        >
          {portalRedirectNotice && (
            <div
              style={{
                width: "100%",
                marginBottom: 24,
                textAlign: "left",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "12px 14px",
                color: "#374151",
                background: "#fafafa",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                This portal could not be opened
              </div>
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {portalRedirectNotice}
              </div>
            </div>
          )}
          <h1 style={{ fontSize: 19, fontWeight: 600, color: "#111827", marginBottom: 26 }}>
            {isCheckingPortalPermissions
              ? "Please wait we are checking your permission"
              : portalPermissionError
                ? "Unable to load permission"
                : "Please select the portal"}
          </h1>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 16,
              width: "100%",
            }}
          >
            {portalOptions.map((portal) => {
              const isDisabled = Boolean(portal.comingSoon);
              const isOptionDisabled = isCheckingPortalPermissions || isDisabled;
              return (
                <button
                  key={portal.id}
                  type="button"
                  onClick={() => handlePortalSelect(portal)}
                  disabled={isOptionDisabled}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    width: 120,
                    minHeight: 110,
                    height: 120,
                    padding: "18px 14px 16px",
                    borderRadius: 12,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    cursor: isOptionDisabled ? "not-allowed" : "pointer",
                    position: "relative",
                    opacity: isOptionDisabled ? 0.55 : 1,
                  }}
                >
                  {isDisabled && (
                    <span
                      style={{
                        position: "absolute",
                        top: -14,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#c83227",
                        color: "#fff",
                        fontSize: 13,
                        lineHeight: "24px",
                        fontWeight: 600,
                        padding: "0 12px",
                        borderRadius: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Coming soon
                    </span>
                  )}
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 60,
                      height: 60,
                      borderRadius: 12,
                      border: "1px solid #e5e7eb",
                      background: "#fafafa",
                    }}
                  >
                    {getPortalIcon(portal.id)}
                  </span>
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: isDisabled ? "#6f6f6f" : "#1f1f1f",
                      textAlign: "center",
                    }}
                  >
                    {portal.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// export { DESK_RBAC } from "feature/authv2/portalDeskConfig";
